import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Sparkles, Crown, Clock, ArrowRight, RefreshCw } from "lucide-react";
import api from "@/lib/api";

/**
 * SubscriptionCard — shows the user's current billing state:
 *   • Pro (monthly)  — Crown icon + amber gradient, "Renews on <date>"
 *   • Pro (annual)   — Crown icon + amber gradient, "Renews on <date>"
 *   • Trialing       — Sparkles icon, "N days left", CTA to upgrade
 *   • Trial ended    — Clock icon + red border, "Renew Pro" CTA
 *   • Free (none)    — Sparkles icon + green outline, "Start trial" CTA
 *
 * Renders on Dashboard (compact `variant="dashboard"`) and Settings
 * (`variant="full"`, larger meta block). Reads /api/billing/status +
 * /api/auth/me for trial_ends_at.
 */
export default function SubscriptionCard({ variant = "full", className = "" }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true });
  const [startingTrial, setStartingTrial] = useState(false);

  const refresh = async () => {
    try {
      const [statusRes, meRes] = await Promise.all([
        api.get("/billing/status"),
        api.get("/auth/me"),
      ]);
      setState({
        loading: false,
        ...statusRes.data,
        trial_ends_at: meRes.data?.trial_ends_at || null,
      });
    } catch {
      setState({ loading: false, tier: "free", is_pro: false, error: true });
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [statusRes, meRes] = await Promise.all([
          api.get("/billing/status"),
          api.get("/auth/me"),
        ]);
        if (!alive) return;
        setState({
          loading: false,
          ...statusRes.data,
          trial_ends_at: meRes.data?.trial_ends_at || null,
        });
      } catch {
        if (alive) setState({ loading: false, tier: "free", is_pro: false, error: true });
      }
    })();
    return () => { alive = false; };
  }, []);

  // One-tap trial start — for `free` view only. Called from the CTA
  // click handler when the user has never had a trial.
  const startTrial = async () => {
    setStartingTrial(true);
    try {
      const { data } = await api.post("/auth/start-trial");
      if (data?.started) {
        toast.success(t("subscription.trial_started", { days: data.days }));
        await refresh();
      } else if (data?.reason === "in_trial") {
        toast.info(t("subscription.already_trialing"));
        await refresh();
      } else if (data?.reason === "already_pro") {
        toast.info(t("subscription.already_pro"));
        await refresh();
      }
    } catch (e) {
      // 402 = already_used → route to pricing so they can pay
      if (e?.response?.status === 402) {
        toast.info(t("subscription.trial_used_redirect"));
        navigate("/pricing");
      } else {
        toast.error(t("subscription.trial_start_failed"));
      }
    } finally { setStartingTrial(false); }
  };

  if (state.loading) {
    return (
      <div className={`card-flat p-5 ${className}`} data-testid="subscription-card-loading">
        <div className="flex items-center gap-3">
          <RefreshCw size={16} className="animate-spin text-[var(--text-muted)]"/>
          <div className="text-sm text-[var(--text-muted)]">{t("subscription.loading")}</div>
        </div>
      </div>
    );
  }

  const view = _resolveView(state, t);
  return <SubscriptionView view={view} variant={variant} className={className}
                           onOneTap={view.key === "free" ? startTrial : null}
                           oneTapBusy={startingTrial} />;
}

function _resolveView(state, t) {
  const now = Date.now();
  const trialEnds = state.trial_ends_at ? new Date(state.trial_ends_at).getTime() : null;
  const inTrial = trialEnds && trialEnds > now;
  const trialExpired = trialEnds && trialEnds <= now;
  const isPro = state.is_pro === true || state.tier === "pro";

  if (isPro) {
    const cycle = state.cycle || state.plan || "monthly";
    const renews = state.current_period_end
      ? new Date(state.current_period_end).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
      : null;
    return {
      key: "pro",
      icon: Crown,
      label: t("subscription.pro_active"),
      badge: cycle === "annual" ? t("subscription.pro_annual") : t("subscription.pro_monthly"),
      meta: renews ? t("subscription.renews_on", { date: renews }) : t("subscription.pro_no_renewal_date"),
      tone: "pro",
      ctaLabel: t("subscription.manage_cta"),
      ctaTo: "/pricing",
      testId: "subscription-card-pro",
    };
  }
  if (inTrial) {
    const daysLeft = Math.max(0, Math.ceil((trialEnds - now) / 86400000));
    return {
      key: "trial",
      icon: Sparkles,
      label: t("subscription.trial_active"),
      badge: t("subscription.trial_badge", { days: daysLeft }),
      meta: t("subscription.trial_meta"),
      tone: "trial",
      ctaLabel: t("subscription.upgrade_cta"),
      ctaTo: "/pricing",
      testId: "subscription-card-trial",
    };
  }
  if (trialExpired) {
    return {
      key: "expired",
      icon: Clock,
      label: t("subscription.trial_ended"),
      badge: t("subscription.trial_ended_badge"),
      meta: t("subscription.trial_ended_meta"),
      tone: "expired",
      ctaLabel: t("subscription.renew_cta"),
      ctaTo: "/pricing",
      testId: "subscription-card-expired",
    };
  }
  return {
    key: "free",
    icon: Sparkles,
    label: t("subscription.free_plan"),
    badge: t("subscription.free_badge"),
    meta: t("subscription.free_meta"),
    tone: "free",
    ctaLabel: t("subscription.start_trial_cta"),
    ctaTo: "/pricing",
    testId: "subscription-card-free",
  };
}

const TONE_STYLES = {
  pro: {
    border: "rgba(224,158,80,0.42)",
    background: "linear-gradient(135deg, rgba(224,158,80,0.10) 0%, rgba(45,90,64,0.08) 100%)",
    iconBg: "linear-gradient(135deg, #2D5A40 0%, #E09E50 100%)",
    iconColor: "#FFFFFF",
    labelColor: "#A16A25",
  },
  trial: {
    border: "rgba(45,90,64,0.30)",
    background: "linear-gradient(135deg, rgba(45,90,64,0.06) 0%, rgba(123,196,144,0.04) 100%)",
    iconBg: "rgba(45,90,64,0.14)",
    iconColor: "#2D5A40",
    labelColor: "#2D5A40",
  },
  expired: {
    border: "rgba(196,91,77,0.35)",
    background: "rgba(196,91,77,0.05)",
    iconBg: "rgba(196,91,77,0.14)",
    iconColor: "#C45B4D",
    labelColor: "#C45B4D",
  },
  free: {
    border: "var(--border-default)",
    background: "rgba(255,255,255,0.40)",
    iconBg: "rgba(45,90,64,0.10)",
    iconColor: "#2D5A40",
    labelColor: "var(--text-secondary)",
  },
};

function SubscriptionView({ view, variant, className, onOneTap, oneTapBusy }) {
  const Icon = view.icon;
  const s = TONE_STYLES[view.tone] || TONE_STYLES.free;
  const compact = variant === "dashboard";
  const isOneTap = typeof onOneTap === "function";
  return (
    <div
      className={`p-5 rounded-2xl border ${className}`}
      style={{ borderColor: s.border, background: s.background }}
      data-testid={view.testId}
    >
      <div className="flex items-center gap-4 flex-wrap">
        <div
          className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: s.iconBg, color: s.iconColor }}
        >
          <Icon size={20} strokeWidth={2.1}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow" style={{ color: s.labelColor }} data-testid="subscription-label">
            {view.label}
          </div>
          <div className="text-base font-medium leading-tight mt-0.5"
               style={{ fontFamily: "var(--font-heading)" }}
               data-testid="subscription-badge">
            {view.badge}
          </div>
          {!compact && (
            <div className="text-xs text-[var(--text-muted)] mt-1.5 font-mono" data-testid="subscription-meta">
              {view.meta}
            </div>
          )}
        </div>
        {isOneTap ? (
          <button
            onClick={onOneTap}
            disabled={oneTapBusy}
            className="btn-primary"
            data-testid="subscription-cta"
          >
            {oneTapBusy ? <RefreshCw size={14} className="animate-spin"/> : null}
            {view.ctaLabel}
            {!oneTapBusy && <ArrowRight size={14}/>}
          </button>
        ) : (
          <Link
            to={view.ctaTo}
            className={view.tone === "pro" ? "btn-ghost" : "btn-primary"}
            data-testid="subscription-cta"
          >
            {view.ctaLabel}
            <ArrowRight size={14}/>
          </Link>
        )}
      </div>
    </div>
  );
}
